<template>
  <b-modal
    v-model="show"
    id="itemModal"
    :title="actionDisplay"
    centered
    scrollable
    @hidden="clear"
  >
    <input
      style="display: none"
      id="fileInput"
      type="file"
      @change="onFileChanged"
    />

    <b-form class="enter-item-form">
      <b-row>
        <b-col>
          <div class="item-modal-picture-container">
            <picture-placeholder
              :textIn="null != item.name ? item.name.toLowerCase() : ''"
              :pictureUrl="itemImageUri"
              :color="'#fff'"
            />
            <div class="hover-upload-button">
              <span class="upload-photo-label" @click="startImageUpload">
                upload<br />photo
              </span>
            </div>
          </div>
        </b-col>

        <b-col sm="8">
          <info-row class="info-main-row" :sizing="3" :title="'Name'">
            <b-col class="input-group">
              <b-form-input
                v-model="itemName"
                class="form-control"
                data-cy="item-name"
                type="text"
                title="Item Name"
                placeholder="Cheeseburger"
                required
                :state="fieldState.itemName"
              />
            </b-col>
          </info-row>

          <info-row class="info-main-row" :sizing="3" :title="'Description'">
            <b-col class="input-group">
              <b-form-input
                v-model="description"
                class="form-control"
                data-cy="item-description"
                type="text"
                required
                :title="'Description'"
                :placeholder="'Typical quarter pounder'"
                :state="fieldState.description"
                :value="description"
              />
            </b-col>
          </info-row>

          <info-row class="info-main-row" :sizing="3" :title="'Rate'">
            <currency-input
              v-if="show"
              data-cy="item-rate"
              ref="rateInput"
              name="Default Rate"
              :amount.sync="defaultRate"
              :fieldState="fieldState.defaultRate"
            />
          </info-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col sm="2"> </b-col>
        <b-col sm="10">
          <!-- temp commented out. needs to be looped in with subscription handlers -->
          <!-- <info-row
            class="info-main-row text-right pl-2 item-toggle"
            :title="$t('unit_type.show_in_db').toLowerCase()"
            :sizing="5"
          >
            <toggle-button
              class="active-toggle"
              :value="show_in_db"
              :sync="true"
              :labels="{ checked: $t('yes'), unchecked: $t('no') }"
              :width="100"
              :height="33"
              style="font-size: 16px;"
              color="#82C7EB"
              @change="show_in_db = (($event.value) ? true : false)"
            />
          </info-row> -->
        </b-col>
      </b-row>

      <br />

      <Taggregator
        ref="tagsTaggregator"
        labelKey="name"
        :label="'Item Tags'"
        :items="getTags"
        :selections.sync="selectedTags"
        :minimum="0"
        :name="'tags'"
        :quickAddCallback="quickAddTag"
        :quickAddMinWordCount="1"
        :quickAddMinWordLength="1"
        :quickAddDescription="'Please provide valid item name'"
        :quickAddHasUniqueName="true"
      >
        <template slot="result" slot-scope="{ result }">
          <span class="result-label">
            {{ result.name }}
          </span>
        </template>
        <template slot="selection" slot-scope="{ selection }">
          {{ selection.name }}
        </template>
      </Taggregator>
    </b-form>

    <div slot="modal-footer" class="w-100">
      <span class="text-danger">{{ warningText }}</span>
      <b-btn
        v-if="item.id"
        class="float-left"
        variant="danger"
        @click="deleteItem"
      >
        Delete Item
      </b-btn>
      <b-btn
        class="float-right"
        variant="success"
        @click="triggerItem"
        data-cy="add-item"
      >
        {{ actionDisplay }}
      </b-btn>
    </div>

    <div v-if="isRequesting" class="item-modal-spinner">
      <spinner-loader />
    </div>

    <image-upload-modal ref="upload-modal" />
  </b-modal>
</template>

<script>
import { mapState, mapActions } from "vuex";
import { PAYMENT_TYPES, PAYMENT_METHODS } from "@/config/constants";
import AlertMixin from "@/mixins/alert-mixin";

export default {
  name: "EnterItemModal",

  components: {
    InfoRow: () => import("@/components/InfoRow"),
    CurrencyInput: () => import("@/components/widgets/CurrencyInput"),
    Taggregator: () => import("@/components/Taggregator"),
    PicturePlaceholder: () => import("@/components/widgets/PicturePlaceholder"),
    SpinnerLoader: () => import("@/components/widgets/SpinnerLoader"),
    ImageUploadModal: () => import("@/components/modals/ImageUploadModal"),
  },

  mixins: [AlertMixin],

  data() {
    return {
      item: {},
      selectedTags: [],
      actionDisplay: "Add Item",
      show: null,
      itemName: "",
      itemImageUri: null,
      defaultRate: 0,
      description: null,
      show_in_db: false,
      warningText: "",
      hideAfter: true,
      selectFile: null,
      fieldState: {
        //true is valid, false shows the red invalid
        defaultRate: null,
        itemName: null,
        description: null,
      },
    };
  },

  computed: {
    ...mapState({
      isRequesting: (state) => state.tags.isRequesting,
      getTags: (state) => state.tags.tags,
    }),
  },

  methods: {
    ...mapActions(["fetchTags", "removeItem", "uploadItemImage"]),

    async open(item = {}) {
      await this.fetchTags();

      this.item = item;
      this.actionDisplay = item.id ? "Update Item" : "Add Item";

      if (item.id) {
        this.itemName = item.name;
        this.description = item.description;
        this.itemImageUri = item.image_md_uri || item.image_uri;
        this.defaultRate = parseFloat(item.default_rate);
        this.show_in_db = !!item.show_in_db;
        this.postProcessItemTags(item);
      }

      this.show = true;
    },

    startImageUpload() {
      if (!("id" in this.item)) {
        this.$swal({
          text: "Please Save First",
          type: "warning",
        });
        return;
      }
      document.getElementById("fileInput").click();
    },

    onFileChanged(event) {
      this.selectedFile = event.target.files[0];

      if (this.selectedFile) {
        this.onUpload();
      }
    },

    onUpload() {
      const uploadSize = this.selectedFile.size / 2048;
      if (uploadSize >= 1000) {
        this.errorNotification("Maximum image size of 2MB");
        return;
      }
      const formData = new FormData();
      formData.append("image", this.selectedFile, this.selectedFile.name);

      this.$Progress.start();

      if (this.$refs["upload-modal"]) {
        this.$refs["upload-modal"].show();
      }

      this.uploadItemImage({ formData, itemId: this.item.id })
        .then((res) => {
          this.itemImageUri = res.image_md_uri;
          this.successNotification(
            `${this.item.name}'s photo has been updated.`
          );
        })
        .catch((err) => {
          this.errorNotification(`${this.item.name}'s photo update failed.`);
        })
        .finally(() => {
          this.$Progress.finish();

          if (this.$refs["upload-modal"]) {
            this.$refs["upload-modal"].hide();
          }
        });
    },

    postProcessItemTags(item) {
      let selectedTags = [];
      if ("item_tag" in item) {
        for (let i = 0; i < item.item_tag.length; i++) {
          let itemTag = item.item_tag[i];
          selectedTags.push({
            id: itemTag.tag_id,
            name: itemTag.name,
          });
        }
      }
      this.$set(this.$data, "selectedTags", selectedTags);
    },

    triggerItem() {
      this.fieldState.defaultRate = !!this.defaultRate && this.defaultRate > 0;
      this.fieldState.description = !!this.description;
      this.fieldState.itemName = !!this.itemName;

      if (
        this.fieldState.defaultRate &&
        this.fieldState.description &&
        this.fieldState.itemName
      ) {
        this.submitItem();
      } else {
        this.warningText =
          "Name, Default Rate, and Description are all required.";
      }
    },

    deleteItem() {
      let confirm_message = "Are you sure?";

      if (this.item.is_used) {
        confirm_message = `Are you sure you want to delete this item?`;
      }

      this.showConfirm(confirm_message).then((result) => {
        if (!result.value) {
          return;
        }

        this.removeItem(this.item.id)
          .then(() => {
            this.infoNotification(`${this.itemName} has been deleted.`);
            this.clear();
            this.show = false;
          })
          .catch((error) => {
            this.errorNotification(
              `Failed to delete item. Items already added to tabs cannot be delete.`
            );
          });
      });
    },

    submitItem() {
      const itemData = {
        id: this.item.id,
        name: this.itemName,
        description: this.description,
        default_rate: this.defaultRate,
        item_tag: this.selectedTags,
        og_item_tag: this.item.item_tag,
        is_locked: 0,
        category_id: 7, // TODO - Grab these from API and look for unclassified.
        show_in_db: this.show_in_db ? 1 : 0,
      };

      this.$emit("submit", itemData);

      if (true == this.hideAfter) {
        this.clear();
        this.show = false;
      }
    },

    quickAddTag(name, items) {
      this.$store
        .dispatch("createTag", {
          name: name,
        })
        .then((response) => {
          items.push(response);
          this.successNotification("Tag succesfully created!");
        })
        .catch((err) => {
          console.dir(err);
          this.errorNotification("Failed to create tag! :(");
        });
    },

    clear() {
      this.defaultRate = 0;
      this.warningText = "";
      this.itemName = "";
      this.description = "";
      this.show_in_db = false;
      this.fieldState = {
        defaultRate: null,
        description: null,
        itemName: null,
      };
      this.item = {};
      this.selectedTags = [];
      this.itemImageUri = null;
    },
  },
};
</script>

<style lang="scss">
.item-modal-spinner {
  position: absolute;
  width: 100%;
  height: 100%;
  left: 0px;
  top: 0px;
  background: rgba(255, 255, 255, 0.7);
}

.item-modal-picture-container {
  position: absolute;
  width: 120px;
  height: 120px;
}

.hover-upload-button {
  position: absolute;
  width: 100%;
  height: 100%;
  background: #555;
  opacity: 0;
  transition: all 0.25s ease-in-out;
  cursor: pointer;
}

.hover-upload-button:hover {
  opacity: 0.8;
}

.upload-photo-label {
  color: #fff;
  position: absolute;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.item-toggle .entry-info {
  padding-left: 18px;
}

/* Added styles to improve modal height and allow Taggregator dropdown visibility */
#itemModal {
  /* Ensure modal takes up more vertical space */
  .modal-content {
    max-height: 90vh;
    min-height: 70vh;
  }

  /* Allow dropdowns inside the modal to overflow without being clipped */
  .modal-body {
    overflow-y: auto;
  }
}
</style>
